import React, { Component } from 'react';
import { identity, constant } from 'fp-ts/function';
import { get, post, userAction } from '@/util/helpers/ajax-helpers';
import { mapObject } from '@/util/helpers/object-helpers';
import {
  DASHBOARD_FILTER_NAME_INPUT,
  DASHBOARD_FILTER_TYPE_BUTTON,
} from '@/constants/test-ids';
import { FILTER_PRESETS_TYPE } from '@/features/dashboard/components/DashboardFilterModal/components/PresetChoices/types';
import style from '../../../../../css/module/dashboard-filters.css';
import flex from '../../../../../css/module/flex.css';
import modal from '../../../../../css/module/modal.css';
import buttons from '../../../../../css/module/buttons.css';
import ColumnName from '../../../../../util/ColumnName';
import ViewRef from '../../../../../util/ViewRef';
import Column from '../../../../../util/Column';
import InputWithFocus from '../../../../../components/InputWithFocus';
import {
  BOOL,
  DATE,
  DATETIME,
  FLOAT,
  INT,
  STRING,
  UUID,
} from '../../../../../util/ColumnType';
import ModalContent from '../../../../../components/ModalContent';
import TableRef from '../../../../../util/TableRef';
import SelectDashboardFilterColumn from '../../../../../components/SelectDashboardFilterColumn';
import { PresetChoicesContainer } from './PresetChoices';

const ID = {
  name: 'UUID',
  examples: 'UUID',
  types: [UUID],
  mapToType: identity,
};
const NUMBER = {
  name: 'Number',
  examples: 'user id, age, count',
  types: [INT, FLOAT],
  mapToType: identity,
};
const TIME = {
  name: 'Date',
  examples: 'date, datetime, date range',
  types: [DATE, DATETIME],
  mapToType: constant(DATE),
};
const TEXT = {
  name: 'Text',
  examples: 'name, city, email address',
  types: [STRING],
  mapToType: identity,
};
const TF = {
  name: 'Boolean',
  examples: 'true or false',
  types: [BOOL],
  mapToType: identity,
};

const typeOptions = [TEXT, NUMBER, TIME, ID, TF];

class DashboardFilterModalContent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      aliases: null, // an array of objects mapping table aliases to table refs, for each query
      name: this.props.existing ? this.props.existing.name() : '',
      type: this.props.existing ? this.getType() : null, // object defined above
      columnType: this.props.existing?.columnType()?.type(),
      queries: [], // list of ViewRef objs
      presets: this.props.existing ? this.props.existing.presets() : [], // list of strings
      savedQueryPresets: this.props.existing?.savedQueryPresets ?? null,
      presetsType: this.props.existing?.savedQueryPresets
        ? FILTER_PRESETS_TYPE.prefilled
        : FILTER_PRESETS_TYPE.manual,
      queryColumns: [], // list of list of Column objs
      columnsBySeries: {}, // series => ColumnName
    };
    this.doneClicked = this.doneClicked.bind(this);
    this.onSelectColumn = this.onSelectColumn.bind(this);
    this.removeColumn = this.removeColumn.bind(this);
    this.setType = this.setType.bind(this);
  }

  // not valid if name / type not selected
  componentDidUpdate() {
    const isValid =
      this.state.name.trim().length > 0 &&
      this.state.type &&
      !this.duplicateName() &&
      Object.keys(this.state.columnsBySeries).length !== 0;

    const isPrefilledPresetsValid =
      this.state.presetsType === FILTER_PRESETS_TYPE.manual ||
      Boolean(this.state.savedQueryPresets?.columnName);

    this.props.onSetIsValid(isValid && isPrefilledPresetsValid);
  }

  componentDidMount() {
    const { dashboard, existing } = this.props;
    userAction('configure-dashboard-filter');
    const existingId = existing ? existing.id() : '';
    get(
      'loading query data',
      `/dashboards/${dashboard.id()}/widget-columns?existing=${existingId}`,
    ).then((json) => {
      const columnBySeries = !existing
        ? {} // user is creating a new filter
        : mapObject(
            json.config.columns_by_series,
            identity,
            (columns) => new ColumnName(columns[0]._alias, columns[0]._name),
          ); // columns is array of size 1
      this.setState({
        queries: json.queries.map(ViewRef.build),
        queryColumns: json.queryColumns.map((cols) => cols.map(Column.build)),
        aliases: json.aliases.map(this.buildAliasObject),
        columnsBySeries: columnBySeries,
      });
      this.props.onSetIsLoading(false);
    });
  }

  // create alias object where each key is an alias and each value is the correspondonding table ref
  buildAliasObject = (aliasObj) => {
    const aliases = {};
    for (const key in aliasObj) {
      aliases[key] = TableRef.buildFromServer(aliasObj[key]);
    }
    return aliases;
  };

  getType() {
    const { existing } = this.props;
    const type = existing.columnType().type();
    return typeOptions.find((o) => o.types.includes(type));
  }

  setPresetsType = (presetsType) => this.setState({ presetsType });

  doneClicked() {
    const { onDone, dashboard, existing } = this.props;

    this.props.onSetIsLoading(true);
    post('saving dashboard filter', `/dashboards/${dashboard.id()}/filters`, {
      existing: existing ? existing.id() : null, // existing is a DashboardFilterRef
      name: this.state.name.trim(),
      columnType: this.state.columnType,
      presets: this.state.presets.map((item) => item.trim()),
      savedQueryPresets: this.state.savedQueryPresets,
      columnNameBySeries: this.state.columnsBySeries,
    }).then(() => {
      userAction('use-configure-dashboard-filter');
      onDone(); // closes modal
    });
  }

  setSavedQueryPresets = (savedQueryPresets) =>
    this.setState({ savedQueryPresets });

  typeDropdown() {
    const display = typeOptions.map((option, i) => {
      return (
        <button
          data-testid={`${DASHBOARD_FILTER_TYPE_BUTTON}:${option.name}`}
          key={i}
          onClick={() => this.setType(option)}
          className={`${buttons.btn} ${flex.alignItemsStart}`}
        >
          <b>{option.name}</b>
          <span>(E.g. {option.examples})</span>
        </button>
      );
    });
    return <div className={buttons.verticalGroup}>{display}</div>;
  }

  setType(option) {
    this.setState({
      type: option,
      columnsBySeries: {}, // reset when type is changed
      columnType: null,
    });
  }

  onSelectColumn(series, column) {
    const { columnsBySeries, type } = this.state;
    const copy = { ...columnsBySeries };
    copy[series] = column.name();
    this.setState({
      columnsBySeries: copy,
      columnType: type.mapToType(column.type().type()),
    });
  }

  queryList() {
    const { columnsBySeries, queries, type, aliases } = this.state;
    return queries.map((q, i) => {
      const series = q.series();
      const selectedCol = columnsBySeries[series];
      const columns = this.state.queryColumns[i];

      return (
        <div className={style.widget} key={`query_${i}`}>
          <p style={{ fontWeight: '600' }}>{q.name()}</p>
          <SelectDashboardFilterColumn
            types={type.types}
            selectedCol={selectedCol}
            columns={columns}
            onClick={(column) => this.onSelectColumn(series, column)}
            aliases={aliases[i]}
            onRemove={() => this.removeColumn(series)}
          />
        </div>
      );
    });
  }

  removeColumn(series) {
    const { columnsBySeries } = this.state;
    const copy = { ...columnsBySeries };
    delete copy[series];
    this.setState({
      columnsBySeries: copy,
    });
    // reset columnType if no more columns are selected
    if (Object.keys(copy).length === 0) {
      this.setState({
        columnType: null,
      });
    }
  }

  duplicateName() {
    const { allFilterNames, existing } = this.props;
    const { name } = this.state;

    if (existing && existing.name().trim() === name.trim()) {
      return false;
    }

    return allFilterNames.some((fn) => fn.trim() === name.trim());
  }

  onPresetsChange(newValue) {
    this.setState({
      presets: newValue.split('\n').map((v) => {
        if (this.state.type === NUMBER) {
          return v.replace(/\D/g, '');
        }
        return v;
      }),
    });
  }

  render() {
    if (this.state.loading) {
      return <ModalContent loading small />;
    }

    return (
      <div>
        <div className="margin-bottom-25">
          <h4>Filter name</h4>
          <div style={{ maxWidth: '300px' }}>
            <div className={modal.inputContainer}>
              <InputWithFocus
                content={this.state.name}
                onChange={(value) =>
                  this.setState({ name: value.toLowerCase() })
                }
                dataTestId={DASHBOARD_FILTER_NAME_INPUT}
                placeholder="E.g. region"
              />
            </div>
          </div>
          <div>
            {this.state.name.length < 2 || !this.duplicateName() ? null : (
              <div
                className="margin-top-10 alert-warning"
                style={{ padding: '10px', borderRadius: '4px' }}
                role="alert"
              >
                Another filter already has this name. Please choose a different
                one
              </div>
            )}
          </div>
        </div>
        {this.state.name.length < 1 && !this.state.type ? null : (
          <div className="margin-bottom-25">
            <h4>Filter type</h4>
            {!this.state.type ? (
              this.typeDropdown()
            ) : (
              <button
                onClick={() => this.setState({ type: null })}
                className={buttons.btn}
              >
                <b>{this.state.type.name}</b>
                <span className={buttons.closePill}>×</span>
              </button>
            )}
          </div>
        )}
        {[ID, TEXT, NUMBER].includes(this.state.type) && (
          <PresetChoicesContainer
            presetsType={this.state.presetsType}
            setPresetsType={this.setPresetsType}
            value={this.state.presets.join('\n')}
            onChange={(e) => this.onPresetsChange(e.target.value)}
            setSavedQueryPresets={this.setSavedQueryPresets}
            savedQueryPresets={this.state.savedQueryPresets}
          />
        )}
        {this.state.type && (
          <div className="margin-bottom-25">
            <h4>Columns this filter should apply to</h4>
            <div>
              {this.state.queries.length === 0 ? (
                <span>
                  Your dashboard has <b>0</b> queries
                </span>
              ) : (
                this.queryList()
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default DashboardFilterModalContent;
